import React, { ReactElement, useEffect, useState } from 'react';
import {
  Button,
  Box,
  Flashbar,
  FormField,
  Header,
  SpaceBetween,
  Modal,
  Cards,
} from '@amzn/awsui-components-react-v3/polaris';
import PropTypes from 'prop-types';
import { AtmsApiClient } from '@amzn/et-console-components';

interface ErrorInfo {
  header: string;
  message: string;
}

interface CardItem {
  header: string;
  description: JSX.Element;
  content: JSX.Element;
}

export const ScoringModel = ({ apiClient }): ReactElement => {
  const FEATURE_IMPORT_SCORING_MAPPING = 'import-scoring-mappings';
  const FEATURE_UPDATE_SCORING_MODEL = 'update-scoring-model';
  const IMPORT_ERROR_HEADER = 'Error importing scoring model file';
  const [showSelfServiceModal, setshowSelfServiceModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<ErrorInfo | null>(null);
  const [scoringModelFile, setScoringModelFile] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successAlert, setSuccessAlert] = useState('');
  const [feature, setFeature] = useState('');
  const [modalHeader, setModalHeader] = useState('');
  const [items, setItems] = useState([] as CardItem[]);

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    setIsSubmitting(true);

    switch (feature) {
      case FEATURE_UPDATE_SCORING_MODEL:
        await apiClient
          .multipartUpload(`/api/scoring/updateScoringModel`, {
            scoringFileName: scoringModelFile,
          })
          .then(response => {
            const frameworkSuffix = response.frameworkType
              ? ` using ${response.frameworkType} Framework`
              : '';
            setSuccessAlert(
              `Successfully imported scoring model: ${response.name} (version ${response.version})${frameworkSuffix}`
            );
            setShowSuccessAlert(true);
          })
          .catch(error => {
            // ATMSScoring returns 422 for several unrelated causes with no code to tell
            // them apart, only a free-text sentence nested under errors[0]. Read that
            // first; error.message is the fallback for all other failures.
            const upstream = error?.details?.errors?.[0];
            const reason =
              upstream?.message ||
              (typeof upstream?.details === 'string' && upstream.details) ||
              (typeof upstream?.details?.details === 'string' && upstream.details.details) ||
              error?.message;

            if (reason) {
              setError({ header: IMPORT_ERROR_HEADER, message: reason });
            } else {
              //Fallback to a generic string if the granular reason is missing.
              setError({
                header: IMPORT_ERROR_HEADER,
                message: `There was a problem importing scoring model file: ${error}`,
              });
            }
          })
          .finally(() => {
            setshowSelfServiceModal(false);
            setIsSubmitting(false);
            setFeature('');
          });
        break;

      case FEATURE_IMPORT_SCORING_MAPPING:
        await apiClient
          .multipartUpload(`/api/scoring/importSampledQAMappings`, {
            mappingsFileName: scoringModelFile,
          })
          .then(() => {
            setSuccessAlert(`Successfully mapped project templates to scoring models`);
            setShowSuccessAlert(true);
          })
          .catch(error => {
            setError({
              header: 'Error mapping scoring models',
              message: `There was a problem importing scoring model mapping file: ${error}`,
            });
          })
          .finally(() => {
            setshowSelfServiceModal(false);
            setIsSubmitting(false);
            setFeature('');
          });
        break;

      default:
        break;
    }
  };

  const handleFileChange = (event): void => {
    setScoringModelFile(event.target.files[0]);
  };

  useEffect(() => {
    const evaluateCardItems = async () => {
      const items: CardItem[] = [];
      items.push({
        header: 'Import an ATMS Scoring Model',
        description: (
          <>
            Import an ATMS Scoring Model for your organization.
            <br />
            For more information about ATMS Scoring Model SOP{' '}
            <a
              href="https://w.amazon.com/bin/view/INTech/TranslationServices/LinguistExperience/ATMS/Docs/ATMS_Scoring/#Scoring"
              target="_blank"
              rel="noopener noreferrer"
            >
              click here
            </a>
            .
            <br />
          </>
        ),
        content: (
          <>
            <Button
              id="importScoringModelButton"
              variant="primary"
              loading={isSubmitting}
              iconName="upload"
              onClick={() => {
                setshowSelfServiceModal(true);
                setFeature(FEATURE_UPDATE_SCORING_MODEL);
                setModalHeader('Upload a scoring model file');
              }}
            >
              Import Scoring Model
            </Button>
          </>
        ),
      });

      try {
        const postMortemScoringSettings = await apiClient.httpGet('/api/postMortemScoringSettings');
        if (postMortemScoringSettings.enabled) {
          items.push({
            header:
              'Map ATMS project templates and scoring models for Sampled Post Delivery QA (SPDQ)',
            description: (
              <>
                For more information about SPDQ,{' '}
                <a
                  href="https://w.amazon.com/bin/view/INTech/TranslationServices/LinguistExperience/ATMS/Docs/ATMS_User_Manual/SampledQA/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  click here
                </a>
                .
                <br />
              </>
            ),
            content: (
              <>
                <Button
                  id="mapScoringModelButton"
                  variant="primary"
                  loading={isSubmitting}
                  iconName="upload"
                  onClick={() => {
                    setshowSelfServiceModal(true);
                    setFeature(FEATURE_IMPORT_SCORING_MAPPING);
                    setModalHeader('Post Mortem Scoring: map project templates and scoring models');
                  }}
                >
                  Map project templates to scoring models
                </Button>
              </>
            ),
          });
        }
      } catch (error) {
        console.error(`error checking post mortem scoring settings, error: ${error}`);
      }
      setItems(items);
    };
    evaluateCardItems();
  }, []);

  return (
    <>
      <Modal
        id="selfServiceModal"
        onDismiss={() => {
          setshowSelfServiceModal(false);
        }}
        visible={showSelfServiceModal}
        closeAriaLabel="Close modal"
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button
                id="cancelButton"
                variant="link"
                onClick={() => {
                  setshowSelfServiceModal(false);
                }}
              >
                Cancel
              </Button>
              <Button
                id="submitScoringModelButton"
                variant="primary"
                disabled={scoringModelFile == null || isSubmitting}
                onClick={() => handleSubmit()}
                loading={isSubmitting}
              >
                Submit
              </Button>
            </SpaceBetween>
          </Box>
        }
        header={<Header variant="h2">{modalHeader}</Header>}
      >
        <FormField>
          <input type="file" name="scoringModelFile" accept=".xlsx" onChange={handleFileChange} />
        </FormField>
      </Modal>
      <SpaceBetween size="m" direction="vertical">
        {showSuccessAlert && (
          <Flashbar
            id={'successAlert'}
            items={[
              {
                type: 'success',
                content: successAlert,
                dismissible: true,
                onDismiss: () => setShowSuccessAlert(false),
              },
            ]}
          />
        )}
        {error != null && (
          <Flashbar
            id={'errorFlashbar'}
            items={[
              {
                header: error.header,
                type: 'error',
                content: error.message,
                dismissible: true,
                onDismiss: () => setError(null),
              },
            ]}
          />
        )}
      </SpaceBetween>
      <header id="header">
        <h1 style={{ marginBottom: 15, padding: 0 }}>Configure Scoring Models</h1>
      </header>
      <Cards
        cardDefinition={{
          header: item => item.header,
          sections: [
            {
              content: item => item.description,
            },
            {
              content: item => item.content,
            },
          ],
        }}
        cardsPerRow={[{ cards: 1 }, { minWidth: 500, cards: 2 }]}
        items={items}
      />
    </>
  );
};

ScoringModel.propTypes = {
  apiClient: PropTypes.object,
};

export const ScoringModelView = (): ReactElement => {
  return ScoringModel({
    apiClient: AtmsApiClient,
  });
};

export default ScoringModelView;
